import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { fetchConfig } from '@theme/utilities';

/**
 * @typedef {{ id: number, o: string[], a: boolean, m: number, p: string }} Variant
 */

/**
 * Halven House product page: gallery, color/size selection, bundle offers, add to bag through
 * Horizon's cart events (drawer + counters update), sticky bar, size guide, Complete the look.
 */
class HhProduct extends HTMLElement {
  /** @type {Variant[]} */
  #variants = [];
  /** @type {string | null} */
  #color = null;
  /** Chosen value per option index (non-color options). @type {Map<number, string>} */
  #picked = new Map();
  /** @type {IntersectionObserver | undefined} */
  #observer;

  connectedCallback() {
    try {
      this.#variants = JSON.parse(this.querySelector('[data-variants]')?.textContent || '[]');
    } catch {
      this.#variants = [];
    }
    const colorOn = this.querySelector('[data-colors] button.on') ?? this.querySelector('[data-colors] button');
    this.#color = colorOn instanceof HTMLElement ? colorOn.dataset.value ?? null : null;
    if (colorOn) colorOn.classList.add('on');

    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
    this.addEventListener('keydown', this.#onKeydown);
    this.querySelector('[data-gal]')?.addEventListener('scroll', this.#onGalleryScroll, { passive: true });
    this.#syncOptions();
    this.#syncAtc();
    this.#setEta();
    this.#observeAtc();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('keydown', this.#onKeydown);
    this.#observer?.disconnect();
  }

  /** @returns {HTMLElement[]} */
  get #groups() {
    return Array.from(this.querySelectorAll('[data-opt]'));
  }

  /** @returns {HTMLElement | null} */
  get #offer() {
    return this.querySelector('[data-offers] .offer.on');
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const t = /** @type {HTMLElement} */ (event.target);

    const color = t.closest('[data-colors] button');
    if (color instanceof HTMLElement) return this.#selectColor(color);

    const opt = t.closest('[data-opt] button');
    if (opt instanceof HTMLButtonElement) return this.#selectOption(opt);

    const tile = t.closest('[data-vt] button');
    if (tile instanceof HTMLButtonElement) return this.#pickTile(tile);

    const offer = t.closest('[data-offers] .offer');
    if (offer instanceof HTMLElement && !t.closest('.units')) return this.#selectOffer(offer);

    const thumb = t.closest('[data-thumbs] button');
    if (thumb) {
      const i = Array.from(thumb.parentElement?.children ?? []).indexOf(thumb);
      return this.#goTo(i);
    }

    if (t.closest('[data-atc]')) return void this.#addToBag();
    if (t.closest('[data-sticky-atc]')) {
      const empty = this.#emptySelect();
      if (empty) {
        empty.closest('.offer')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        empty.closest('.sel')?.classList.add('bad');
        return;
      }
      const missing = this.#missingGroup();
      if (missing) {
        missing.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return this.#shake(missing);
      }
      return void this.#addToBag();
    }

    if (t.closest('[data-open-fit]')) {
      event.preventDefault();
      return this.querySelector('[data-fit-modal]')?.classList.add('open');
    }
    const modal = this.querySelector('[data-fit-modal]');
    if (modal && (t === modal || t.closest('[data-fit-close]'))) return modal.classList.remove('open');

    const step = t.closest('[data-rail-step]');
    if (step instanceof HTMLElement) {
      const rail = this.querySelector('[data-rail]');
      rail?.scrollBy({ left: rail.clientWidth * 0.9 * Number(step.dataset.railStep), behavior: 'smooth' });
      return;
    }

    const quick = t.closest('[data-quick]');
    if (quick instanceof HTMLButtonElement) return void this.#quickAdd(quick);
    const qa = this.querySelector('[data-qa]');
    if (qa && (t === qa || t.closest('[data-qa-close]'))) return void (/** @type {HTMLElement} */ (qa).hidden = true);
  };

  /** Per-piece selects inside a bundle offer. @param {Event} event */
  #onChange = (event) => {
    const select = /** @type {HTMLElement} */ (event.target);
    if (!(select instanceof HTMLSelectElement) || !select.closest('.units')) return;
    const sel = select.closest('.sel');
    sel?.classList.remove('bad');
    const error = /** @type {HTMLElement | null} */ (this.querySelector('[data-error]'));
    if (error) error.hidden = true;
    const dot = sel?.querySelector('[data-dot]');
    if (dot instanceof HTMLElement) dot.style.background = select.selectedOptions[0]?.dataset.hex ?? '#ddd';
    if (sel?.classList.contains('sel--color')) {
      // jump the gallery to that color's photo
      const ci = Number(select.dataset.oi);
      const v = this.#variants.find((x) => x.o[ci] === select.value && x.m);
      const imgs = Array.from(this.querySelectorAll('[data-gal] img'));
      const i = v ? imgs.findIndex((img) => /** @type {HTMLElement} */ (img).dataset.media === String(v.m)) : -1;
      if (i >= 0) this.#goTo(i);
    }
  };

  /** Arrow keys / Enter on the offer radios. @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const offer = /** @type {HTMLElement} */ (event.target).closest?.('[data-offers] .offer');
    if (!(offer instanceof HTMLElement) || event.target !== offer) return;
    const all = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-offers] .offer')));
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.#selectOffer(offer);
    } else if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const next = all[(all.indexOf(offer) + (event.key === 'ArrowDown' ? 1 : -1) + all.length) % all.length];
      if (next) {
        this.#selectOffer(next);
        next.focus();
      }
    }
  };

  /**
   * Color photo tile inside a bundle piece: mirror it into that piece's color select, which add-to-bag reads.
   * @param {HTMLButtonElement} tile
   */
  #pickTile(tile) {
    const row = tile.closest('.unit');
    const select = row?.querySelector('.sel--color select');
    if (!row || !(select instanceof HTMLSelectElement)) return;
    row.querySelectorAll('[data-vt] button').forEach((b) => {
      b.classList.toggle('on', b === tile);
      b.setAttribute('aria-pressed', String(b === tile));
    });
    const name = row.querySelector('[data-vtn]');
    if (name) name.textContent = tile.dataset.v ?? '';
    select.value = tile.dataset.v ?? '';
    select.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /** @returns {HTMLSelectElement | undefined} first per-piece select left empty in the chosen bundle */
  #emptySelect() {
    return /** @type {HTMLSelectElement[]} */ (Array.from(this.#offer?.querySelectorAll('.units select') ?? [])).find((x) => !x.value);
  }

  /* ---------- gallery ---------- */

  /** @param {number} index */
  #goTo(index) {
    const gal = /** @type {HTMLElement | null} */ (this.querySelector('[data-gal]'));
    const img = gal?.children[index];
    if (!gal || !(img instanceof HTMLElement)) return;
    if (gal.scrollWidth > gal.clientWidth + 4) {
      gal.scrollTo({ left: index * gal.clientWidth, behavior: 'smooth' });
    } else {
      img.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  #onGalleryScroll = () => {
    const gal = /** @type {HTMLElement | null} */ (this.querySelector('[data-gal]'));
    if (!gal || !gal.clientWidth) return;
    const i = Math.round(gal.scrollLeft / gal.clientWidth);
    this.querySelectorAll('[data-dots] i').forEach((d, j) => d.classList.toggle('on', i === j));
    const thumbs = /** @type {HTMLElement | null} */ (this.querySelector('[data-thumbs]'));
    Array.from(thumbs?.children ?? []).forEach((b, j) => {
      b.classList.toggle('on', i === j);
      if (i === j && thumbs && b instanceof HTMLElement) {
        thumbs.scrollTo({ left: b.offsetLeft - thumbs.clientWidth / 2 + b.clientWidth / 2, behavior: 'smooth' });
      }
    });
  };

  /* ---------- variants ---------- */

  get #colorIndex() {
    const el = /** @type {HTMLElement | null} */ (this.querySelector('[data-colors]'));
    return el ? Number(el.dataset.index) : -1;
  }

  /** @param {HTMLElement} button */
  #selectColor(button) {
    this.querySelectorAll('[data-colors] button').forEach((b) => {
      b.classList.toggle('on', b === button);
      b.setAttribute('aria-pressed', String(b === button));
    });
    this.#color = button.dataset.value ?? null;
    const name = this.querySelector('[data-color-name]');
    if (name) name.textContent = this.#color;
    const v = this.#variants.find((x) => this.#matches(x, new Map()));
    if (v?.m) {
      const imgs = Array.from(this.querySelectorAll('[data-gal] img'));
      const i = imgs.findIndex((img) => /** @type {HTMLElement} */ (img).dataset.media === String(v.m));
      if (i >= 0) this.#goTo(i);
    }
    this.#syncOptions();
    this.#syncAtc();
  }

  /** @param {HTMLButtonElement} button */
  #selectOption(button) {
    const group = /** @type {HTMLElement} */ (button.closest('[data-opt]'));
    group.querySelectorAll('button').forEach((b) => b.classList.toggle('on', b === button));
    this.#picked.set(Number(group.dataset.index), button.dataset.value ?? '');
    const name = group.previousElementSibling?.querySelector('[data-opt-name]');
    if (name) name.textContent = (button.dataset.value ?? '') + (button.classList.contains('off') ? ' — sold out' : '');
    this.#syncOptions();
    this.#syncAtc();
  }

  /** @param {HTMLElement} offer */
  #selectOffer(offer) {
    this.querySelectorAll('[data-offers] .offer').forEach((o) => {
      o.classList.toggle('on', o === offer);
      o.setAttribute('aria-checked', String(o === offer));
      o.setAttribute('tabindex', o === offer ? '0' : '-1');
    });
    this.#syncAtc();
  }

  /**
   * True when the variant fits the chosen color and every entry of `picked`.
   * @param {Variant} v
   * @param {Map<number, string>} picked
   */
  #matches(v, picked) {
    const ci = this.#colorIndex;
    if (ci >= 0 && this.#color !== null && v.o[ci] !== this.#color) return false;
    for (const [i, value] of picked) if (v.o[i] !== value) return false;
    return true;
  }

  /** Strike option values with no available variant for the other current choices. */
  #syncOptions() {
    for (const group of this.#groups) {
      const index = Number(group.dataset.index);
      for (const b of /** @type {HTMLButtonElement[]} */ (Array.from(group.querySelectorAll('button')))) {
        const test = new Map(this.#picked);
        test.set(index, b.dataset.value ?? '');
        const ok = this.#variants.some((v) => v.a && this.#matches(v, test));
        b.classList.toggle('off', !ok);
      }
    }
  }

  /** @returns {HTMLElement | undefined} first option group still waiting for a choice */
  #missingGroup() {
    return this.#groups.find((g) => !this.#picked.has(Number(g.dataset.index)));
  }

  /** @returns {Variant | undefined} */
  get #variant() {
    if (this.#missingGroup()) return undefined;
    return this.#variants.find((x) => this.#matches(x, this.#picked));
  }

  #syncAtc() {
    const v = this.#variant ?? this.#variants.find((x) => this.#matches(x, new Map())) ?? this.#variants[0];
    const offer = this.#offer;
    const price = offer?.dataset.total ?? v?.p ?? '';
    const label = `${this.dataset.label ?? 'Add to bag'} — ${price}`;
    this.querySelectorAll('[data-atc-text]').forEach((el) => (el.textContent = label));
    const priceEl = this.querySelector('[data-price]');
    if (priceEl && v?.p) priceEl.textContent = v.p;
    const sub = this.querySelector('[data-sticky-sub]');
    if (sub && offer) {
      sub.textContent = `${offer.dataset.title ?? ''} · ${price}`;
    } else if (sub) {
      const parts = [this.#color];
      for (const g of this.#groups) parts.push(this.#picked.get(Number(g.dataset.index)) ?? `Select ${g.dataset.label}`);
      parts.push(price);
      sub.textContent = parts.filter(Boolean).join(' · ');
    }
  }

  /** @param {HTMLElement} group */
  #shake(group) {
    group.animate(
      [{ transform: 'translateX(0)' }, { transform: 'translateX(-6px)' }, { transform: 'translateX(6px)' }, { transform: 'translateX(0)' }],
      { duration: 300 }
    );
    const name = group.previousElementSibling?.querySelector('[data-opt-name]');
    if (name) name.textContent = `Please select ${group.dataset.label}`;
  }

  /* ---------- cart ---------- */

  async #addToBag() {
    const error = /** @type {HTMLElement | null} */ (this.querySelector('[data-error]'));
    if (error) error.hidden = true;
    if (this.#offer?.querySelector('.units')) return this.#addBundle(error);
    const missing = this.#missingGroup();
    if (missing) return this.#shake(missing);
    const v = this.#variant;
    if (!v || !v.a) {
      if (error) {
        error.textContent = 'This combination is sold out. Please choose another.';
        error.hidden = false;
      }
      return;
    }
    const qty = Number(this.#offer?.dataset.qty || 1);
    const buttons = Array.from(this.querySelectorAll('[data-atc], [data-sticky-atc]'));
    buttons.forEach((b) => b.classList.add('busy'));
    try {
      await this.#post([{ id: v.id, quantity: qty }]);
      this.querySelectorAll('[data-atc-text]').forEach((el) => (el.textContent = 'Added ✓'));
      setTimeout(() => this.#syncAtc(), 1600);
    } catch (e) {
      if (error) {
        error.textContent = e instanceof Error ? e.message : String(e);
        error.hidden = false;
      }
    } finally {
      buttons.forEach((b) => b.classList.remove('busy'));
    }
  }

  /**
   * Bundle: one variant per piece row (its own color/size), grouped into cart lines.
   * @param {HTMLElement | null} error
   */
  async #addBundle(error) {
    const rows = Array.from(this.#offer?.querySelectorAll('[data-unit]') ?? []);
    /** @type {Map<number, number>} */
    const counts = new Map();
    for (const row of rows) {
      const selects = /** @type {HTMLSelectElement[]} */ (Array.from(row.querySelectorAll('select')));
      const empty = selects.find((x) => !x.value);
      if (empty) {
        empty.closest('.sel')?.classList.add('bad');
        empty.focus();
        if (error) {
          const n = /** @type {HTMLElement} */ (row).dataset.unit;
          error.textContent = `Please choose a ${empty.dataset.name}${rows.length > 1 ? ` for #${n}` : ''}.`;
          error.hidden = false;
        }
        return;
      }
      const v = this.#variants.find((x) => selects.every((sel) => x.o[Number(sel.dataset.oi)] === sel.value));
      if (!v || !v.a) {
        if (error) {
          error.textContent = `${selects.map((x) => x.value).join(' / ')} is sold out. Please choose another.`;
          error.hidden = false;
        }
        return;
      }
      counts.set(v.id, (counts.get(v.id) ?? 0) + 1);
    }
    const buttons = Array.from(this.querySelectorAll('[data-atc], [data-sticky-atc]'));
    buttons.forEach((b) => b.classList.add('busy'));
    try {
      await this.#post(Array.from(counts, ([id, quantity]) => ({ id, quantity })));
      this.querySelectorAll('[data-atc-text]').forEach((el) => (el.textContent = 'Added ✓'));
      setTimeout(() => this.#syncAtc(), 1600);
    } catch (e) {
      if (error) {
        error.textContent = e instanceof Error ? e.message : String(e);
        error.hidden = false;
      }
    } finally {
      buttons.forEach((b) => b.classList.remove('busy'));
    }
  }

  /**
   * Adds lines, then resolves Horizon's CartLinesUpdateEvent so the drawer opens and every
   * cart view re-renders (same contract as the theme's own product form).
   * @param {{ id: number, quantity: number }[]} items
   */
  async #post(items) {
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
      .filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((i) => ({ merchandiseId: String(i.id), quantity: i.quantity })),
        promise: deferred.promise,
      })
    );
    try {
      // Horizon's cart_add_url already ends in .js
      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchConfig('json', {
          body: JSON.stringify({ items, sections: sectionIds.join(','), sections_url: window.location.pathname }),
        }),
      }).then((r) => r.json());
      if (response.status) throw new Error(response.description || response.message || 'Could not add to bag');
      const cart = await fetch(`${Theme.routes.cart_url}.js`).then((r) => r.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'hh-product',
          itemCount: items.reduce((n, i) => n + i.quantity, 0),
          productId: this.dataset.productId,
          sections: response.sections,
          didError: false,
        },
      });
      if (!document.querySelector('cart-drawer-component')) window.location.href = Theme.routes.cart_url;
    } catch (e) {
      deferred.reject(e);
      this.dispatchEvent(new CartErrorEvent({ error: e instanceof Error ? e.message : String(e), code: 'INVALID' }));
      throw e;
    }
  }

  /**
   * Complete the look "+": single variant → add; otherwise pick a size (first available color).
   * @param {HTMLButtonElement} button
   */
  async #quickAdd(button) {
    if (button.classList.contains('ok')) return;
    const handle = button.dataset.quick;
    const product = await fetch(`${(window.Shopify?.routes?.root ?? '/').replace(/\/$/, '')}/products/${handle}.js`).then((r) => r.json());
    /** @type {{ id: number, available: boolean, options: string[], title: string }[]} */
    const variants = product.variants;
    const done = async (/** @type {number} */ id) => {
      await this.#post([{ id, quantity: 1 }]);
      button.classList.add('ok');
      button.textContent = '✓';
    };
    if (variants.length === 1 && variants[0]) return done(variants[0].id);

    const names = product.options.map((/** @type {{ name: string }} */ o) => o.name);
    const ci = names.indexOf('Color');
    const firstColor = ci >= 0 ? variants.find((v) => v.available)?.options[ci] : null;
    const pick = variants.filter((v) => ci < 0 || v.options[ci] === firstColor);
    const qa = /** @type {HTMLElement} */ (this.querySelector('[data-qa]'));
    const title = qa.querySelector('[data-qa-title]');
    if (title) title.textContent = `${product.title}${firstColor ? ` — ${firstColor}` : ''}`;
    const opts = /** @type {HTMLElement} */ (qa.querySelector('[data-qa-opts]'));
    opts.replaceChildren(
      ...pick.map((v) => {
        const b = document.createElement('button');
        b.type = 'button';
        b.textContent = v.options.filter((_, i) => i !== ci).join(' / ') || v.title;
        b.disabled = !v.available;
        b.addEventListener('click', async () => {
          qa.hidden = true;
          await done(v.id);
        });
        return b;
      })
    );
    qa.hidden = false;
  }

  /* ---------- misc ---------- */

  /** Today + min..max business days, weekends skipped. */
  #setEta() {
    const el = /** @type {HTMLElement | null} */ (this.querySelector('[data-eta]'));
    if (!el) return;
    const add = (/** @type {number} */ n) => {
      const d = new Date();
      while (n > 0) {
        d.setDate(d.getDate() + 1);
        if (d.getDay() % 6) n--;
      }
      return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    };
    el.textContent = `${add(Number(el.dataset.min || 3))} – ${add(Number(el.dataset.max || 6))}`;
  }

  /** Sticky bar appears once the main button has scrolled above the viewport. */
  #observeAtc() {
    const atc = this.querySelector('[data-atc]');
    const sticky = /** @type {HTMLElement | null} */ (this.querySelector('[data-sticky]'));
    if (!atc || !sticky) return;
    this.#observer = new IntersectionObserver(([entry]) => {
      const show = !!entry && !entry.isIntersecting && entry.boundingClientRect.top < 0;
      sticky.classList.toggle('show', show);
      sticky.setAttribute('aria-hidden', String(!show));
      sticky.querySelector('[data-sticky-atc]')?.setAttribute('tabindex', show ? '0' : '-1');
    });
    this.#observer.observe(atc);
  }
}

if (!customElements.get('hh-product')) customElements.define('hh-product', HhProduct);
